import type { IUseCase } from '@/shared/contracts/use-case.contract';

import type {
  GetStoreItemsRequest,
  GetStoreItemsResponse,
  StoreItem,
  StoreItemsByCategory,
} from '../types/get-store-items';
import { ENDPOINT } from '../types/get-store-items';
import { normalizeAddonSelectMax } from '../utils/addon';
import { HttpMethod, type IHttpClient } from '@/shared/contracts/http-client.contract';

export type UseCaseInput = GetStoreItemsRequest;
export type UseCaseOutput = GetStoreItemsResponse;
export class GetStoreItemsUseCase implements IUseCase<UseCaseInput, UseCaseOutput> {
  private client: IHttpClient;

  constructor(client: IHttpClient) {
    this.client = client;
  }

  async execute(input: UseCaseInput): Promise<UseCaseOutput> {
    const { slug, canal } = input;

    if (!slug) {
      throw new Error('Slug is required');
    }

    const response = await this.client.sendRequest<UseCaseOutput, UseCaseInput>({
      method: HttpMethod.GET,
      endpoint: `${ENDPOINT}/${slug}/items${canal ? `?canal=${canal}` : ''}`,
    });

    // Mapeia o teto individual por complemento (`select_max`, issue 311) vindo do
    // endpoint para um número confiável, deixando o valor já pronto para o
    // seletor de complementos aplicar o bloqueio/aviso por complemento.
    return {
      ...response,
      items: this.mapItemsAddonSelectMax(response.items),
    };
  }

  private mapItemsAddonSelectMax(items: StoreItemsByCategory): StoreItemsByCategory {
    if (!items) return items;

    return Object.fromEntries(
      Object.entries(items).map(([category, storeItems]) => [
        category,
        Array.isArray(storeItems)
          ? storeItems.map(item => this.mapItemAddonSelectMax(item))
          : storeItems,
      ])
    );
  }

  private mapItemAddonSelectMax(item: StoreItem): StoreItem {
    if (!Array.isArray(item.addon_categories)) return item;

    return {
      ...item,
      addon_categories: this.mapCategoriesAddonSelectMax(item.addon_categories),
      // Complementos das opções do combo configurável (PRAT-20) seguem a mesma
      // normalização dos complementos do produto.
      ...(Array.isArray(item.combo_groups)
        ? {
            combo_groups: item.combo_groups.map(group => ({
              ...group,
              options: Array.isArray(group.options)
                ? group.options.map(option => ({
                    ...option,
                    addon_categories: Array.isArray(option.addon_categories)
                      ? this.mapCategoriesAddonSelectMax(option.addon_categories)
                      : [],
                  }))
                : [],
            })),
          }
        : {}),
    };
  }

  private mapCategoriesAddonSelectMax(
    categories: StoreItem['addon_categories']
  ): StoreItem['addon_categories'] {
    return categories.map(category => ({
      ...category,
      addons: Array.isArray(category.addons)
        ? category.addons.map(addon => ({
            ...addon,
            select_max: normalizeAddonSelectMax(addon.select_max),
          }))
        : category.addons,
    }));
  }
}
